<template>
  <div>
    <section id="over-rijnja" class="content-wrapper page">
      <section id="" class="row header">
        <div class="col text">
          <h1>Rijnja.nl API documentation</h1>
          <p>
            The Rijnja.nl API can be used to submit orders to Rijnja, as well as
            retrieve the order status for previously submitted orders.
          </p>
          <p>
            Communication with the API is done through JSON over secure HTTP
            (port 443).
          </p>
          <p>
            This document was last updated on 2024-07-03.
          </p>
          <h2>History</h2>
          <ul>
            <li>2024-07-03: Updated endpoints; added deprecation info</li>
            <li>2022-03-28: Added webhook</li>
            <li>2021-12-13: Cosmetic changes</li>
            <li>
              2020-01-07: Added file upload functionality. Added authentication
              methods to order_submit script.
            </li>
            <li>
              2019-01-31: First version
            </li>
          </ul>

          <h1 class="sub">HTTP Connection</h1>
          <p>
            Clients can connect to the API scripts through HTTPS on port 443
            (standard). Clients must support TLS 1.2
          </p>
          <p>
            Requests must have <code>content-type 'application/json'</code>,
            with the JSON in the request body (described below).
          </p>
          <h1 class="sub">JSON</h1>
          <p>
            Input and response of the scripts is in JSON. The expected inputs
            and returned outputs are described using JSON schema.
          </p>
          <p>
            JSON schema is a method of describing JSON structures using JSON.
          </p>
          <p>
            For more information, refer to the
            <a href="https://json-schema.org/" target="_blank" class="link"
              >documentation</a
            >.
          </p>
          <h1 class="sub">Authentication</h1>
          <p>The API supplies the following functionality to the client:</p>
          <h2>Requesting an account</h2>
          <p>
            Customers who want to implement a client will need to request a
            client name and key from Rijnja.<br />To do so, send an email to
            <a href="mailto:helpdesk@rijnja.nl" class="link"
              >helpdesk@rijnja.nl</a
            >
            with the following information:
          </p>
          <ul>
            <li>Your company name, website, address info.</li>
            <li>The IP address(es) the new client will be connecting from.</li>
            <li>
              (Optional) An email address that will be used for testing. Orders
              sent with this email as the contact will not be printed, but can
              still be tracked as normal.
            </li>
          </ul>
          <h1 class="sub">API functionality</h1>
          <p>The API supplies the following functionality to the client:</p>
          <h2>Dry run order submission</h2>
          <p>
            As the script for submitting an order will immediately submit it,
            this script is supplied as a means of testing a client during setup.
            <br />It will perform the same functions as the submit script, but
            without actually submitting the order.<br />This way a developer can
            test a client implementation.
          </p>
          <table class="url-table">
            <tr>
              <td class="title">URL</td>
            </tr>
            <tr>
              <td>{{ host }}/api/customer/validate-order</td>
            </tr>
            <tr>
              <td class="title">Input JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/api_order_submit.schema.json`"
                  target="_blank"
                  class="link"
                  >{{ host }}/api/schema/api_order_submit.schema.json
                </a>
              </td>
            </tr>
            <tr>
              <td class="title">Response JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/response_order_submit.schema.json`"
                  target="_blank"
                  class="link"
                  >https://www.rijnja.nl/api/schema/response_order_submit.schema.json
                </a>
              </td>
            </tr>
          </table>

          <h2>Submitting an order</h2>
          <p>
            This script may be used to submit an order to Rijnja. The order
            needs to be described using JSON according to the schema listed
            below. The description will contain the number of files in the
            order, and for each file the required print specifications and
            number of copies, as well as a URL where the source file can be
            downloaded from by the API.
          </p>
          <p>
            After the order has been received, the server will send an email to
            the user (using the email address supplied in the JSON), confirming
            the subission and providing a PDF with the order overview.
          </p>
          <p>
            The specified file can be a ZIP archive, in which case each file
            contained therein will be printed using the listed specifications.
            Note that the number of copies specified will be used for each file
            in the archive! In the order overview, each file will be listed
            seperately.
          </p>

          <table class="url-table">
            <tr>
              <td class="title">URL</td>
            </tr>
            <tr>
              <td>{{ host }}/api/customer/order</td>
            </tr>
            <tr>
              <td class="title">Input JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/api_order_submit.schema.json`"
                  target="_blank"
                  class="link"
                  >{{ host }}/api/schema/api_order_submit.schema.json
                </a>
              </td>
            </tr>
            <tr>
              <td class="title">Response JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/response_order_submit.schema.json`"
                  target="_blank"
                  class="link"
                  >{{ host }}/api/schema/response_order_submit.schema.json
                </a>
              </td>
            </tr>
          </table>

          <h2>Retrieving order status</h2>
          <p>
            After a client has submitted an order, this script can be used to
            retrieve the current status of the order.
          </p>

          <table class="url-table">
            <tr>
              <td class="title">URL</td>
            </tr>
            <tr>
              <td>{{ host }}/api/customer/order-status</td>
            </tr>
            <tr>
              <td class="title">Input JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/api_order_status.schema.json`"
                  target="_blank"
                  class="link"
                  >{{ host }}/api/schema/api_order_status.schema.json
                </a>
              </td>
            </tr>
            <tr>
              <td class="title">Response JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/response_order_status.schema.json`"
                  target="_blank"
                  class="link"
                  >{{ host }}/api/schema/response_order_status.schema.json
                </a>
              </td>
            </tr>
          </table>

          <p>
            Alternatively a webhook can be configured for you if you provide us
            with your endpoint URL and optionally a username/password.
            <br />Proper operation of the webhook can be tested by submitting a
            (test) order request to {{ host }}/api/api_webhook_test.php.
            <br />The submitted order will not be processed.
          </p>

          <h2>Uploading files</h2>
          <p>
            Should a client wish to upload files directly, instead of providing
            them through a web-accessible URL, this script can be used.
          </p>
          <p>
            A single file can be uploaded per call, although multiple files can
            be combined in a zip archive, and then unpacked automatically by the
            server.
          </p>
          <p>
            For each file thus supplied, a virtual downloadurl will be returned,
            which can then be used in a call to the order submit script.
            Uploaded files can only be used once, by the API account that
            uploaded them and have to be used within 24 hours.
          </p>
          <p>
            Unused files will be authomatically deleted within 48 hours.
          </p>

          <table class="url-table">
            <tr>
              <td class="title">URL</td>
            </tr>
            <tr>
              <td>{{ host }}/api/customer/file-upload</td>
            </tr>
            <tr>
              <td class="title">Input JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/api_file_upload.schema.json`"
                  target="_blank"
                  class="link"
                  >{{ host }}/api/schema/api_file_upload.schema.json
                </a>
              </td>
            </tr>
            <tr>
              <td class="title">Response JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/response_file_upload.schema.json`"
                  target="_blank"
                  class="link"
                  >{{ host }}/api/schema/response_file_upload.schema.json
                </a>
              </td>
            </tr>
          </table>

          <h2>Adding files to a user's shopping cart</h2>

          <table class="url-table">
            <tr>
              <td class="title">URL</td>
            </tr>
            <tr>
              <td>{{ host }}/api/customer/add-to-cart</td>
            </tr>
            <tr>
              <td class="title">Input JSON schema</td>
            </tr>
            <tr>
              <td>
                <a
                  :href="`${host}/api/schema/add_to_cart.schema.json`"
                  target="_blank"
                  class="link"
                  >{{ host }}/api/schema/add_to_cart.schema.json
                </a>
              </td>
            </tr>
          </table>

          <h1 class="sub">Examples</h1>
          <h2>Example of a JSON-formatted order</h2>
          <code>
            <pre>
{
	"authentication": {
		"appname" : "example.com",
		"appkey" : "0123456789ABCDEF0123456789ABCDEF0123456789ABCDEF0123456789ABCDEF"
	},
	"order" : {
		"invoiceaddress": {
			"companyname": "Your Company",
			"attn": "Invoice Name",
			"address": "Street of example 5",
			"postalcode": "1234 AB",
			"city": "Utrecht"
		},
		"deliveryaddress": {
			"companyname": "Recipient Company",
			"attn": "Recipient Name",
			"address": "Street of example 5",
			"postalcode": "1234 AB",
			"city": "Utrecht"
		},
		"reference": "Order Reference",
		"project": "Project",
		"contact": "Your Name",
		"emailaddress": "example@example.com",
		"tel": "0301234567",
		"deliverydatetime": "2019-01-31T12:00:00",
		"orderline": [
			{
				"quantity": 1,
				"downloadurl": "http://www.example.com/file.pdf",
				"filename": "file.pdf",
				"method": "zwart/wit dubbelzijdig",
				"size": "A4",
				"media": "Papier 80g",
				"finishing": {
					"Nieten": true
				}
			}
		]
	}
}
            </pre>
          </code>
          <h1 class="sub">Deprecated endpoints</h1>
          <p>
            The following endpoints are deprecated as of 2024-07-03 but will be
            supported until further notice.
          </p>
          <code>
            <pre
              >{{ host }}/api/api_order_validate.php
{{ host }}/api/api_order_submit.php
{{ host }}/api/api_file_upload.php
{{ host }}/api/api_order_status.php
            </pre>
          </code>
        </div>
      </section>
    </section>
    <site-footer></site-footer>
  </div>
</template>

<script>
import SiteFooter from '../../components/SiteFooter.vue'
export default {
  components: { SiteFooter },
  data() {
    return {
      host: window.location.origin
    }
  }
}
</script>

<style lang="scss" scoped>
@use 'sass:math';

.sub {
  padding-top: 40px;
}

.page h2 {
  font-size: 18px;
}

.url-table {
  background-color: #f6f6f6;
  border-radius: 5px;
  width: 450px;
  padding: 10px;
  margin: 20px 0;
  box-shadow: 0 0 10px 0px rgba(128, 128, 128, 20%);
  td {
    padding: 3px;
  }
  .title {
    font-weight: 500;
  }
}

pre {
  box-shadow: 0 0 10px 0px hsla(0deg, 0%, 50%, 20%);
  border-radius: 5px;
  padding: 15px 30px;
  display: inline-block;
  font-size: 0.8rem;
  tab-size: 2;
  background-color: #f6f6f6;
  margin: 20px 0;
}
</style>
